import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Avatar,
  Rating,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Stack,
  IconButton,
  Badge,
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as TimeIcon,
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  Luggage as LuggageIcon,
  Pets as PetsIcon,
  SmokingRooms as SmokingIcon,
  EventSeat as SeatIcon,
  AttachMoney as MoneyIcon,
  VerifiedUser as VerifiedIcon,
  AddCircleOutline,
  RemoveCircleOutline,
  Close as CloseIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from '../../contexts/LoadingContext';
import apiService from '../../services/apiService';
import MessageButton from '../../components/Chat/MessageButton';
import TranslatingText from '../../components/Common/TranslatingText';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useSocket } from '../../contexts/SocketContext';
import SocialShareModal from '../../components/Common/SocialShareModal';

interface Ride {
  id: string;
  driver: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    driverRating: number;
    profilePicture?: string;
    totalTripsAsDriver: number;
    isLicenseVerified?: boolean;
    cancellationBadgeUntil?: string | Date;
  };
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  originDetails?: string;
  destinationDetails?: string;
  departureDateTime: string;
  pricePerPerson: number;
  totalSeats: number;
  availableSeats: number;
  estimatedDurationMinutes?: number;
  allowsLargeLuggage: boolean;
  allowsPets: boolean;
  allowsSmoking: boolean;
  additionalNotes?: string;
  vehicle?: {
    make: {
      id: string;
      name: string;
    };
    model: {
      id: string;
      name: string;
    };
    color: string;
    year?: number;
    isDocsVerified?: boolean;  // Add verification badge
  };
  status?: string;
}

const RideDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { setLoading } = useLoading();
  const { openProfile } = useUserProfile();

  const [ride, setRide] = useState<Ride | null>(location.state?.ride || null);
  const [isLoading, setIsLoading] = useState(!location.state?.ride);
  const [error, setError] = useState('');
  const [bookingOpen, setBookingOpen] = useState(false);
  const [seatsToBook, setSeatsToBook] = useState(1);
  const [bookingNotes, setBookingNotes] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      const hasPreview = location.state?.ride && location.state.ride.id === id;
      if (hasPreview) {
        setRide(location.state.ride);
        setIsLoading(false);
        loadRideDetails(false); // Fetch in background silently
      } else {
        setRide(null);
        setIsLoading(true);
        loadRideDetails(true); // Fetch with loader
      }
    }
  }, [id]);

  const { socket } = useSocket();

  useEffect(() => {
    if (location.state?.showBooking) {
      setBookingOpen(true);
      if (location.state?.initialSeats) {
        setSeatsToBook(location.state?.initialSeats);
      }
    }
  }, [location.state?.showBooking, location.state?.initialSeats]);

  useEffect(() => {
    if (!socket || !id) return;

    const handleUpdate = (data: any) => {
      // If the event specifies a rideId, verify it matches
      if (data && data.rideId && data.rideId !== id) return;
      loadRideDetails(false); // reload in background silently
    };

    socket.on('ride_updated', handleUpdate);
    socket.on('ride_cancelled', handleUpdate);
    socket.on('booking_created', handleUpdate);
    socket.on('booking_updated', handleUpdate);

    return () => {
      socket.off('ride_updated', handleUpdate);
      socket.off('ride_cancelled', handleUpdate);
      socket.off('booking_created', handleUpdate);
      socket.off('booking_updated', handleUpdate);
    };
  }, [socket, id]);

  const loadRideDetails = async (showLoader = true) => {
    if (!id) return;

    if (showLoader) {
      setIsLoading(true);
      setLoading(true, t('rideDetails.loading'));
    }

    try {
      const response = await apiService.get(`/rides/${id}`);
      if (response.success) {
        setRide(response.data.ride);
      } else {
        setError(t('rideDetails.loadError'));
      }
    } catch (error: any) {
      console.error('Load ride details error:', error);
      setError(t('rideDetails.loadErrorRetry'));
    } finally {
      if (showLoader) {
        setIsLoading(false);
        setLoading(false);
      }
    }
  };

  const handleBookRide = async () => {
    if (!ride || !user) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    setIsBooking(true);
    setLoading(true, t('rideDetails.booking'));

    try {
      const response = await apiService.post('/bookings', {
        rideId: ride.id,
        seatsBooked: seatsToBook,
        specialRequests: bookingNotes || undefined,
      });

      if (response.success) {
        setBookingOpen(false);
        setSuccessMessage(
          t('rideDetails.bookingSuccess', {
            count: seatsToBook,
            price: ride.pricePerPerson * seatsToBook,
          })
        );

        // Reload ride details to show updated booking status
        await loadRideDetails();
      } else {
        setError(response.message || t('rideDetails.bookingError'));
      }
    } catch (error: any) {
      console.error('Book ride error:', error);
      setError(error.response?.data?.message || t('rideDetails.bookingErrorRetry'));
    } finally {
      setIsBooking(false);
      setLoading(false);
    }
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !ride) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/find-rides')}>
          {t('rideDetails.backToSearch')}
        </Button>
      </Container>
    );
  }

  if (!ride) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info">{t('rideDetails.notFound')}</Alert>
      </Container>
    );
  }

  const { date, time } = formatDateTime(ride.departureDateTime);
  const isDriverRide = user?.id === ride.driver.id;

  const userExistingBookings = (ride as any).bookings?.filter(
    (b: any) => b.passengerId === user?.id && b.status !== 'CANCELLED'
  );
  const userBookedSeatsCount = userExistingBookings?.reduce(
    (sum: number, b: any) => sum + b.seatsBooked, 0
  ) || 0;

  const handleDuplicateRide = () => {
    navigate('/create-ride', { state: { duplicateFrom: ride } });
  };

  const handleEditRide = () => {
    if (ride.availableSeats < ride.totalSeats) {
      alert(t('rideDetails.editWithBookingsError'));
      return;
    }
    navigate('/create-ride', { state: { editRide: ride } });
  };

  const handleCancelRide = async () => {
    const hasPassengers = ride.availableSeats < ride.totalSeats;

    let confirmMessage = t('rideDetails.cancelConfirmation');

    if (hasPassengers) {
      confirmMessage = t('rideDetails.cancelWithPassengersWarning',
        'Warning: This ride has active bookings. Canceling it will result in a 30-day cancellation badge (C tag) on your profile. Do you want to proceed?');
    } else {
      confirmMessage = t('rideDetails.cancelNoPassengersPolicy',
        'Canceling this ride will refund 1 credit ONLY if this is your only ride scheduled for today. Do you want to proceed?');
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true, t('rideDetails.cancelling'));
      // Use DELETE for driver-initiated cancellation as per backend routes
      const response = await apiService.delete(`/rides/${ride.id}`);
      if (response.success) {
        setSuccessMessage(t('rideDetails.cancelSuccess'));
        loadRideDetails();
      } else {
        setError(response.message || t('rideDetails.cancelError'));
      }
    } catch (err: any) {
      console.error('Cancel ride error:', err);
      setError(err.response?.data?.message || t('rideDetails.cancelError'));
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    setShareModalOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Main Content (Left Column) */}
        <Grid size={{ xs: 12, md: 7.5 }}>
          <Stack spacing={3}>
            {/* Route & Schedule Card */}
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.5px' }}>
                    {ride.originCity.name} → {ride.destinationCity.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TimeIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    <span style={{ fontWeight: 600, color: '#1976d2' }}>{date}</span> at <span style={{ fontWeight: 600, color: '#1976d2' }}>{time}</span>
                    {ride.estimatedDurationMinutes && ` • Est. ${formatDuration(ride.estimatedDurationMinutes)}`}
                  </Typography>
                </Box>
                <IconButton
                  color="primary"
                  onClick={handleShare}
                  sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.15)' } }}
                  title={t('rideDetails.share', 'Share Ride')}
                >
                  <ShareIcon />
                </IconButton>
              </Box>

              <Divider sx={{ my: 2.5 }} />

              {/* Vertical Timeline */}
              <Box sx={{ pl: 2, position: 'relative' }}>
                {/* Visual Connector Line */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '21px',
                    top: '24px',
                    bottom: '24px',
                    width: '3px',
                    borderRadius: '4px',
                    background: 'linear-gradient(180deg, #1976d2 0%, #4caf50 100%)',
                  }}
                />

                {/* Pickup / Origin */}
                <Box sx={{ display: 'flex', gap: 3.5, mb: 4, position: 'relative' }}>
                  <Box
                    sx={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      border: '3px solid #fff',
                      boxShadow: '0 0 0 3px #1976d2',
                      mt: '5px',
                      zIndex: 1,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {t('rideDetails.pickup', 'Pickup')} • {ride.originCity.name}
                    </Typography>
                    {ride.originDetails ? (
                      <Box sx={{ mt: 1, p: 2, bgcolor: '#f4f6f8', borderRadius: 2, borderLeft: '3px solid #1976d2' }}>
                        <TranslatingText text={ride.originDetails} variant="body2" color="text.secondary" />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                        No specific pickup details provided
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Drop-off / Destination */}
                <Box sx={{ display: 'flex', gap: 3.5, position: 'relative' }}>
                  <Box
                    sx={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      border: '3px solid #fff',
                      boxShadow: '0 0 0 3px #4caf50',
                      mt: '5px',
                      zIndex: 1,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {t('rideDetails.dropoff', 'Drop-off')} • {ride.destinationCity.name}
                    </Typography>
                    {ride.destinationDetails ? (
                      <Box sx={{ mt: 1, p: 2, bgcolor: '#f4f6f8', borderRadius: 2, borderLeft: '3px solid #4caf50' }}>
                        <TranslatingText text={ride.destinationDetails} variant="body2" color="text.secondary" />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                        No specific drop-off details provided
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Driver & Ride Details Card */}
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
              {/* Driver and Vehicle Grid */}
              <Grid container spacing={3}>
                {/* Driver Info */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', mb: 2, letterSpacing: '0.5px' }}>
                    {t('rideDetails.driver')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        ride.driver.cancellationBadgeUntil && new Date(ride.driver.cancellationBadgeUntil) > new Date() ? (
                          <Typography
                            sx={{
                              width: 20,
                              height: 20,
                              bgcolor: '#ef4444',
                              color: 'white',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '11px',
                              fontWeight: '900',
                              border: '2px solid white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                            title={t('rideDetails.cancellationBadgeTooltip', 'Recent Cancellation Badge')}
                          >
                            C
                          </Typography>
                        ) : null
                      }
                    >
                      <Avatar
                        src={ride.driver.profilePicture}
                        sx={{ width: 60, height: 60, cursor: 'pointer', border: '2px solid #e0e0e0', '&:hover': { borderColor: 'primary.main' } }}
                        onClick={() => openProfile(ride.driver.id)}
                      >
                        {(ride.driver.preferredName || ride.driver.firstName || '?')[0]}{(ride.driver.lastName || '?')[0]}
                      </Avatar>
                    </Badge>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {ride.driver.preferredName || ride.driver.firstName} {ride.driver.lastName[0]}.
                        </Typography>
                        {ride.driver.isLicenseVerified && (
                          <Chip
                            icon={<VerifiedIcon style={{ fontSize: 14, color: 'white' }} />}
                            label="License"
                            size="small"
                            color="success"
                            sx={{ height: 18, fontSize: '10px', color: 'white', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Rating value={ride.driver.driverRating || 0} readOnly size="small" precision={0.1} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          ({ride.driver.totalTripsAsDriver || 0} trips)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {user?.id !== ride.driver.id && (
                    <Box sx={{ mt: 2 }}>
                      <MessageButton
                        userId={ride.driver.id}
                        userName={`${ride.driver.preferredName || ride.driver.firstName || ''} ${ride.driver.lastName ? ride.driver.lastName.charAt(0) + '.' : ''}`}
                        contextType="ride"
                        contextId={ride.id}
                        label={t('rideDetails.askAboutRide')}
                        size="small"
                      />
                    </Box>
                  )}
                </Grid>

                {/* Vehicle Info */}
                {ride.vehicle && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', mb: 2, letterSpacing: '0.5px' }}>
                      {t('rideDetails.vehicle')}
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #e0e0e0', height: '100%', minHeight: '92px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CarIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {t(`vehicle.colorNames.${ride.vehicle.color}`, ride.vehicle.color)} {ride.vehicle.make?.name || ''} {ride.vehicle.model?.name || ''}
                          </Typography>
                          {ride.vehicle.year && (
                            <Typography variant="caption" color="text.secondary">
                              Year: {ride.vehicle.year}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {ride.vehicle.isDocsVerified && (
                        <Box sx={{ mt: 1.5 }}>
                          <Chip
                            icon={<VerifiedIcon style={{ fontSize: 14, color: 'white' }} />}
                            label="Verified Vehicle"
                            size="small"
                            color="success"
                            sx={{ height: 20, fontSize: '11px', color: 'white', fontWeight: 600 }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Amenities */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', mb: 1.5, letterSpacing: '0.5px' }}>
                  Preferences & Amenities
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <Chip
                    icon={<LuggageIcon style={{ fontSize: 16 }} />}
                    label={t(ride.allowsLargeLuggage ? 'rideDetails.largeLuggageAllowed' : 'rideDetails.noLargeLuggage')}
                    color={ride.allowsLargeLuggage ? 'success' : 'default'}
                    variant={ride.allowsLargeLuggage ? 'filled' : 'outlined'}
                    sx={{ borderRadius: '8px', color: ride.allowsLargeLuggage ? 'white' : 'text.primary', fontWeight: 600 }}
                  />
                  <Chip
                    icon={<PetsIcon style={{ fontSize: 16 }} />}
                    label={t(ride.allowsPets ? 'rideDetails.petsAllowed' : 'rideDetails.noPets')}
                    color={ride.allowsPets ? 'success' : 'default'}
                    variant={ride.allowsPets ? 'filled' : 'outlined'}
                    sx={{ borderRadius: '8px', color: ride.allowsPets ? 'white' : 'text.primary', fontWeight: 600 }}
                  />
                  <Chip
                    icon={<SmokingIcon style={{ fontSize: 16 }} />}
                    label={t(ride.allowsSmoking ? 'rideDetails.smokingAllowed' : 'rideDetails.noSmoking')}
                    color={ride.allowsSmoking ? 'warning' : 'default'}
                    variant={ride.allowsSmoking ? 'filled' : 'outlined'}
                    sx={{ borderRadius: '8px', color: ride.allowsSmoking ? 'white' : 'text.primary', fontWeight: 600 }}
                  />
                </Box>
              </Box>

              {ride.additionalNotes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', mb: 1, letterSpacing: '0.5px' }}>
                    {t('rideDetails.additionalNotes')}
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: '#fefefe', borderLeft: '4px solid #757575', borderRadius: 1 }}>
                    <TranslatingText text={ride.additionalNotes} variant="body2" color="text.secondary" />
                  </Box>
                </>
              )}
            </Paper>
          </Stack>
        </Grid>

        {/* Booking Sidebar (Right Column) */}
        <Grid size={{ xs: 12, md: 4.5 }}>
          <Paper
            elevation={4}
            sx={{
              p: 4,
              position: 'sticky',
              top: 24,
              borderRadius: 3,
              boxShadow: '0 12px 36px rgba(0,0,0,0.08)',
              border: '1px solid #eaeaea',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: 900, color: 'primary.main' }}>
                ${ride.pricePerPerson}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('rideDetails.perPerson')}
              </Typography>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SeatIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t('rideDetails.availableSeats')}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {ride.availableSeats} of {ride.totalSeats} seats
              </Typography>
            </Box>

            {/* Simple Visual Progress Bar for Seats */}
            <Box sx={{ width: '100%', height: '8px', bgcolor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden', mb: 4 }}>
              <Box
                sx={{
                  width: `${(ride.availableSeats / ride.totalSeats) * 100}%`,
                  height: '100%',
                  bgcolor: ride.availableSeats > 0 ? 'success.main' : 'error.main',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>

            {isDriverRide ? (
              <Stack spacing={2}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('rideDetails.yourRide')}
                </Alert>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleEditRide}
                  disabled={ride.availableSeats < ride.totalSeats || !(ride.status === 'ACTIVE' || ride.status === 'PENDING_VERIFICATION')}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 700 }}
                >
                  {t('rideDetails.editRide')}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCancelRide}
                  color="error"
                  disabled={!(ride.status === 'ACTIVE' || ride.status === 'PENDING_VERIFICATION')}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 700 }}
                >
                  {t('rideDetails.cancelRide')}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleDuplicateRide}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 700 }}
                >
                  {t('rideDetails.duplicateRide')}
                </Button>
              </Stack>
            ) : ride.availableSeats === 0 ? (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                {t('rideDetails.fullyBooked')}
              </Alert>
            ) : (
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => setBookingOpen(true)}
                disabled={!user}
                sx={{
                  py: 2,
                  borderRadius: 2.5,
                  fontWeight: 800,
                  fontSize: '16px',
                  boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {t(user ? 'rideDetails.bookThisRide' : 'rideDetails.loginToBook')}
              </Button>
            )}

            {!user && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, textAlign: 'center', fontWeight: 500 }}>
                {t('rideDetails.pleaseLoginToBook')}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onClose={() => !isBooking && setBookingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('rideDetails.bookThisRide')}
          <IconButton onClick={() => setBookingOpen(false)} disabled={isBooking}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {userBookedSeatsCount > 0 && (
              <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: '1.2em' } }}>
                {t('rideDetails.alreadyBookedWarning', {
                  count: userBookedSeatsCount,
                  defaultValue: `You already have ${userBookedSeatsCount} seat(s) booked on this ride. Are you sure you want to book more seats?`
                })}
              </Alert>
            )}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{t('rideDetails.numberOfSeats')}</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    onClick={() => setSeatsToBook(Math.max(1, seatsToBook - 1))}
                    disabled={seatsToBook <= 1 || isBooking}
                  >
                    <RemoveCircleOutline />
                  </IconButton>
                  <Typography variant="h6" component="span" sx={{ minWidth: '2rem', textAlign: 'center' }}>
                    {seatsToBook}
                  </Typography>
                  <IconButton
                    onClick={() => setSeatsToBook(Math.min(ride.availableSeats, seatsToBook + 1))}
                    disabled={seatsToBook >= ride.availableSeats || isBooking}
                  >
                    <AddCircleOutline />
                  </IconButton>
                </Stack>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                {t('rideDetails.available', { count: ride.availableSeats })}
              </Typography>
            </Paper>

            <TextField
              label={t('rideDetails.additionalNotesOptional')}
              value={bookingNotes}
              onChange={(e) => setBookingNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder={t('rideDetails.specialRequestsPlaceholder')}
              disabled={isBooking}
            />

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t('rideDetails.totalPrice')}:</Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                ${ride.pricePerPerson * seatsToBook}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleBookRide}
            disabled={isBooking}
          >
            {isBooking ? <CircularProgress size={26} /> : t('rideDetails.bookSeats', { count: seatsToBook })}
          </Button>
        </DialogActions>
      </Dialog>

      <SocialShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        type="ride"
        data={ride}
      />
    </Container>
  );
};

export default RideDetailsPage;
